/**
 * Which ground the document stands on.
 *
 * The base coat in `styles.css` is Arcade. The document is stamped before
 * styles load so a lazy route never flashes a retired dark ground.
 *
 * Two Arcade grounds, and the second is a workshop-only exception: `arcade`
 * is the sunbleached ground a player stands on, `paper` is that same world on
 * white, for a page whose subject IS the ground. The sketchbook needs it —
 * exhibits painted #faf6ec vanish into a page painted #faf6ec. No
 * player-facing route may take `paper`.
 *
 * It has to be the document rather than the page's own wrapper because
 * `/dev/sketch` is a large lazy chunk, and until it lands no wrapper can paint
 * its paper ground.
 *
 * The stamp runs on import rather than from a call in `main.tsx`, because an
 * import is evaluated before the importing module's body: listing this first
 * puts it on the document before anything else the app draws. The stylesheet
 * itself is linked from `index.html` and paints first, under the launch cover,
 * on the base coat, which is Arcade; only the paper workshop changes ground
 * after that first frame. `App.tsx` re-runs it on navigation to and from the
 * paper workshop.
 */
const GROUNDS = new Map<string, string>([['/dev/sketch', 'paper']]);

export interface GroundLocation {
  readonly pathname: string;
  /** The query, with or without its leading `?`. */
  readonly search?: string | undefined;
}

export function groundFor({ pathname }: GroundLocation): string {
  return GROUNDS.get(pathname) ?? 'arcade';
}

export function applyGround(location: GroundLocation | string): void {
  const where = typeof location === 'string' ? { pathname: location } : location;
  const root = document.documentElement;
  const ground = groundFor(where);
  root.dataset.ground = ground;
}

if (typeof document !== 'undefined') {
  applyGround(globalThis.location);
}
